import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";

// DOT "Limited Quantity" surface mark (49 CFR 172.315) — a square-on-point with solid black
// triangles at the top and bottom and a white band across the middle. This is the mark that
// replaced ORM-D and is what carriers require on small-arms ammunition shipped by ground.
export const LimitedQuantityMark = ({ size = 300 }) => (
  <svg width={size} height={size} viewBox="0 0 220 220" role="img"
       aria-label="DOT Limited Quantity marking" data-testid="limited-quantity-mark">
    {/* diamond border, white fill */}
    <polygon points="110,8 212,110 110,212 8,110" fill="#ffffff" stroke="#000000" strokeWidth="9" strokeLinejoin="miter" />
    {/* top black triangle */}
    <polygon points="110,14 186,90 34,90" fill="#000000" />
    {/* bottom black triangle */}
    <polygon points="110,206 186,130 34,130" fill="#000000" />
  </svg>
);

export default function ShipLabelPrint() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const printed = useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/orders/${orderId}/label-print`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setErr(formatError(e.response?.data?.detail) || "Couldn't load the label.");
      }
    })();
    return () => { alive = false; };
  }, [orderId]);

  // Auto-open the print dialog once content is ready (label image loaded, or no label).
  const triggerPrint = () => {
    if (printed.current) return;
    printed.current = true;
    setTimeout(() => window.print(), 350);
  };

  useEffect(() => {
    if (data && !data.label_url) triggerPrint();
  }, [data]);

  if (err) {
    return <div style={{ padding: 40, fontFamily: "monospace", color: "#111" }} data-testid="ship-label-print-error">{err}</div>;
  }
  if (!data) {
    return <div style={{ padding: 40, fontFamily: "monospace", color: "#111" }} data-testid="ship-label-print-loading">Loading label…</div>;
  }

  return (
    <div className="slp-root" data-testid="ship-label-print">
      <style>{`
        .slp-root { background:#f4f4f4; color:#111; }
        .slp-toolbar { position:sticky; top:0; display:flex; gap:12px; align-items:center; justify-content:space-between;
          background:#111; color:#fff; padding:12px 18px; font-family:monospace; text-transform:uppercase; letter-spacing:.08em; font-size:12px; }
        .slp-toolbar button { background:#cfe86b; color:#141a05; border:none; padding:8px 16px; font-weight:700;
          text-transform:uppercase; letter-spacing:.06em; cursor:pointer; font-family:monospace; }
        .slp-sheet { background:#fff; margin:24px auto; padding:28px; max-width:820px; box-shadow:0 2px 12px rgba(0,0,0,.15); }
        .slp-label-img { display:block; max-width:100%; margin:0 auto; }
        .slp-mark-wrap { text-align:center; }
        .slp-mark-title { font-family:monospace; text-transform:uppercase; letter-spacing:.1em; font-size:13px; color:#333; margin-bottom:8px; }
        .slp-mark-instr { font-family:sans-serif; font-size:14px; line-height:1.5; color:#222; max-width:560px; margin:18px auto 0; }
        .slp-mark-instr b { color:#000; }
        @media print {
          .slp-root { background:#fff; }
          .slp-toolbar { display:none; }
          .slp-sheet { box-shadow:none; margin:0; padding:0; max-width:none; }
          .slp-page-break { break-before:page; page-break-before:always; }
          @page { size:auto; margin:0.4in; }
        }
      `}</style>

      <div className="slp-toolbar">
        <span data-testid="slp-title">
          Print label{data.is_ammunition ? " + hazmat marking" : ""} · {data.listing_title || data.order_id}
        </span>
        <button onClick={() => window.print()} data-testid="slp-print-btn">Print</button>
      </div>

      {/* PAGE 1 — carrier shipping label */}
      <section className="slp-sheet" data-testid="slp-label-page">
        {data.label_url ? (
          <img src={data.label_url} alt="Shipping label" className="slp-label-img"
               data-testid="slp-label-img" onLoad={triggerPrint} onError={triggerPrint} />
        ) : (
          <div style={{ fontFamily: "monospace", textAlign: "center", padding: "40px 0" }} data-testid="slp-label-missing">
            Label image unavailable — check the order, or contact support.
          </div>
        )}
        <div style={{ fontFamily: "monospace", fontSize: 12, textAlign: "center", marginTop: 14, color: "#555" }}>
          {data.carrier}{data.service ? ` · ${data.service}` : ""}{data.tracking_code ? ` · ${data.tracking_code}` : ""}
        </div>
      </section>

      {/* PAGE 2 — DOT Limited Quantity hazmat marking (ammunition only) */}
      {data.is_ammunition && (
        <section className="slp-sheet slp-page-break slp-mark-wrap" data-testid="slp-ormd-page">
          <div className="slp-mark-title">Limited Quantity — affix to the outside of the box</div>
          <LimitedQuantityMark size={320} />
          <p className="slp-mark-instr">
            This shipment contains <b>small-arms ammunition</b>. Cut out and affix this
            <b> DOT Limited Quantity</b> marking to the outside of the package, next to the shipping
            label, before you ship. The mark must be at least <b>100 mm (4 in)</b> on each side —
            enlarge if needed. Ship <b>UPS or FedEx Ground only</b> (USPS does not accept ammunition).
          </p>
        </section>
      )}
    </div>
  );
}
